<template>
    <div class="group ds-schedule-day-cell item" :class="[{
        'ds-schedule-day-cell-filled': shiftsNumber,
        'ds-schedule-day-cell-empty': !shiftsNumber,
    }, cellHighlightClass]" :data-schedule-day="day">
        <div v-if="hasOvertime && authStore.permissions?.extended_cell_info && !scheduleStore.isCellHighlighting" class="absolute top-[2px] right-[2px] group/overtime w-1.5 h-1.5 flex items-center justify-center">
            <span class="block w-[5px] h-[5px] rounded-full bg-violet ring-1 ring-white cursor-help"></span>
            <OvertimeTooltip :shifts="shifts" :schedule="schedule" />
        </div>
        <div v-else-if="hasOvertime && authStore.permissions?.extended_cell_info" class="absolute top-[2px] right-[2px] group/overtime h-[5px] w-[5px] cursor-help">
            <span v-if="hasMajorTimeDeviation" class="block h-[5px] w-[5px] rounded-full bg-warning cursor-help"></span>
            <span v-else class="absolute -top-px right-0 cursor-help font-serif text-[11px] leading-none text-gray">i</span>
            <OvertimeTooltip :shifts="shifts" :schedule="schedule" />
        </div>
        <div v-else-if="scheduleStore.isCellHighlighting && hasMajorTimeDeviation && authStore.permissions?.extended_cell_info" class="absolute top-[2px] right-[2px] h-1.5 w-1.5 rounded-full bg-warning">
        </div>
        <div v-if="notHired"></div>
        <div v-else-if="shiftsNumber" class="h-full">
            <div v-if="isSingleDayStateDocumentShift" class="text-[11px] items-center justify-center flex flex-col h-full" :style="{ 'color': dayType?.color }">
                <div>{{ dayType?.short_name }}</div>
                <div v-if="dayStateDuration" class="leading-4">{{ dayStateDuration }}</div>
            </div>
            <div v-else-if="!isWorkday" class="text-[11px] items-center justify-center flex h-full" :style="{ 'color': dayType?.color }">
                {{ dayType?.short_name }}
            </div>
            <div v-else-if="shiftsNumber > 1" class="text-gray text-sm flex flex-col items-center justify-center">
                <div class="h-4">{{ shiftsNumber }}</div>
                <div class="text-xs h-4">смены</div>
                <div class="text-[11px] pt-[3px] text-right semibold">{{ duration }}</div>
            </div>
            <div v-else class="ds-schedule-shift group">
                <div class="ds-schedule-shift-times" :class="{ outer: outShift }">
                    <div class="ds-schedule-shift-time ds-schedule-shift-time-plan">{{ planStart }}</div>
                    <div class="ds-schedule-shift-time ds-schedule-shift-time-fact hidden group-hover:block"
                        :class="`text-${shifts[0].violation?.start_status}`" v-if="factStart && !outShift">
                        {{ factStart }}</div>
                    <div class="ds-schedule-shift-time ds-schedule-shift-time-plan">{{ planEnd }}</div>
                    <div class="ds-schedule-shift-time ds-schedule-shift-time-fact hidden group-hover:block"
                        :class="`text-${shifts[0].violation?.end_status}`" v-if="factEnd && !outShift">{{ factEnd }}
                    </div>
                    <div class="text-[11px] text-center semibold"
                        :class="outShift ? 'pt-1 leading-[13px]' : 'pt-3 group-hover:invisible'">{{ duration }}</div>
                    <div v-if="outShift" class="pt-0.5 flex justify-center leading-none">
                        <icon width="7" height="9">
                            <IconOuter />
                        </icon>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="flex h-full items-center">
            <div v-if="schedule.is_vacancy || schedule.department.id !== scheduleStore.currentDepartment?.id">
                <!-- if employee from another department or from exchange --></div>
            <div v-else-if="dayjs(scheduleStore.currentScheduleDate).date(day).isBefore(dayjs())" class="text-[11px]"
                :style="{ 'color': holidayColor }">ВЫХ</div>
            <icon v-else>
                <IconPlus />
            </icon>
        </div>
        <div class="h-[3px] absolute bottom-0 w-full" v-if="!notHired" :class="usesCellHighlighting ? cellUnderlineClass : dayColor">
        </div>
        <Tooltip :text="`Смена в ${outDepartment}`" v-if="outShift" class="tooltip" />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Schedule } from '@/types'
import IconPlus from '@/ui/icons/IconPlus.vue'
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import Tooltip from './Tooltip.vue'
import OvertimeTooltip from './OvertimeTooltip.vue'
import IconOuter from '@/ui/icons/IconOuter.vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useAuthStore } from '@/stores/authStore'
import { formatDecimalHours, formatOptionalDecimalHoursForSchedule } from '@/utils/duration'

const departmentStore = useDepartmentStore()
const scheduleStore = useScheduleStore()
const authStore = useAuthStore()

interface Props {
    schedule: Schedule,
    day: number
}

dayjs.extend(utc)

const { schedule, day } = defineProps<Props>()


const notHired = computed(() => dayjs(schedule.hired_at).isAfter(scheduleStore.currentScheduleDate.set('date', day)) || dayjs(schedule.fired_at).isBefore(scheduleStore.currentScheduleDate.set('date', day)))

const shifts = computed(() => schedule.shifts.filter(shift => {
    if (schedule.department.id === scheduleStore.currentDepartment?.id) {
        return +dayjs(shift.date).format('D') === day
    } else {
        return +dayjs(shift.date).format('D') === day && shift.department === scheduleStore.currentDepartment?.id
    }
}))

const shiftsNumber = computed(() => shifts.value.length)

const dayType = computed(() => {
    if (shifts.value.length) return scheduleStore.dayTypes.find((daytype) => daytype.id === shifts.value[0]?.day_type)
})

const isWorkday = computed(() => {
    if (dayType.value) return dayType.value.code === 'WD'
})

const isSingleDayStateDocumentShift = computed(() => shiftsNumber.value === 1 && Boolean(shifts.value[0]?.day_state_document))

const dayStateDuration = computed(() => formatOptionalDecimalHoursForSchedule(shifts.value[0]?.duration))

const holidayColor = computed(() => scheduleStore.dayTypes.find(item => item.code === 'H')?.color)

const outShift = computed(() => { if (shifts.value.length) return scheduleStore.currentDepartment?.id !== shifts.value[0]?.department })
const outDepartment = computed(() => { if (shifts.value.length) return departmentStore.departments.find(item => item.id === shifts.value[0]?.department)?.name })
const hasOvertime = computed(() => shifts.value.some(shift => shift.is_overtime))
const plannedShifts = computed(() => shifts.value.filter(shift => shift.plan_work_start_at && shift.plan_work_end_at))
const isFutureDay = computed(() => dayjs(scheduleStore.currentScheduleDate).date(day).isAfter(dayjs(), 'day'))
const cellUnderlineStatus = computed(() => {
    if (plannedShifts.value.every(shift => !shift.fact_work_start_at && !shift.fact_work_end_at)) {
        return isFutureDay.value ? '' : 'blue'
    }
    const hasRedCondition = plannedShifts.value.some((shift) => {
        if (!shift.fact_work_start_at || !shift.fact_work_end_at) return true
        const lateStart = dayjs.utc(shift.fact_work_start_at).diff(dayjs.utc(shift.plan_work_start_at), 'minute')
        const earlyEnd = dayjs.utc(shift.plan_work_end_at).diff(dayjs.utc(shift.fact_work_end_at), 'minute')
        return lateStart >= 5 || earlyEnd >= 5
    })
    return hasRedCondition ? 'warning' : 'ok'
})
const hasMajorTimeDeviation = computed(() => plannedShifts.value.some((shift) => {
    const earlyStart = shift.fact_work_start_at && dayjs.utc(shift.plan_work_start_at).diff(dayjs.utc(shift.fact_work_start_at), 'minute') >= 60
    const lateEnd = shift.fact_work_end_at && dayjs.utc(shift.fact_work_end_at).diff(dayjs.utc(shift.plan_work_end_at), 'minute') >= 60
    return Boolean(earlyStart || lateEnd)
}))
const usesCellHighlighting = computed(() => (
    scheduleStore.isCellHighlighting
    && Boolean(authStore.permissions?.extended_cell_info)
    && plannedShifts.value.length > 0
    && !outShift.value
))
const isOvertimeCombination = computed(() => {
    if (!usesCellHighlighting.value) return false

    if (shifts.value.length === 1) return shifts.value[0]?.is_overtime
    if (shifts.value.length !== 2) return false

    const overtimeShifts = shifts.value.filter((shift) => shift.is_overtime)
    if (overtimeShifts.length !== 1) return false

    const otherShift = shifts.value.find((shift) => !shift.is_overtime)
    return Boolean(
        otherShift?.day_state_document
        || (otherShift?.plan_work_start_at && otherShift?.plan_work_end_at),
    )
})
const cellHighlightClass = computed(() => {
    return isOvertimeCombination.value ? 'schedule-cell-highlight-overtime-combination' : ''
})
const cellUnderlineClass = computed(() => `schedule-cell-underline-${cellUnderlineStatus.value}`)

const planStart = computed(() => (shifts.value[0]?.plan_work_start_at) ? dayjs.utc(shifts.value[0]?.plan_work_start_at).format('HH:mm') : '')
const planEnd = computed(() => (shifts.value[0]?.plan_work_end_at) ? dayjs.utc(shifts.value[0]?.plan_work_end_at).format('HH:mm') : '')

// Вывести суммарное время смен в ячейке
const duration = computed(() => {
    if(shifts.value.length > 1) {
        const sumHours = shifts.value.reduce((sum, shift) => {
            const duration = (shift.duration) ? +shift.duration : 0
            return sum + duration
        }, 0)
        return formatDecimalHours(sumHours, 'schedule')
    } else if(shifts.value.length === 1) {
        const total = shifts.value[0]?.duration
        if(!total) return null
        return formatDecimalHours(+total, 'schedule')
    }
} )

const factStart = computed(() => {
    if (shifts.value[0]?.fact_work_start_at) {
        return dayjs.utc(shifts.value[0]?.fact_work_start_at).format('HH:mm')
    } else if (!shifts.value[0]?.fact_work_start_at && shifts.value[0]?.violation?.start_status === msg[0]) {
        return '??:??'
    }
})
const factEnd = computed(() => {
    if (shifts.value[0]?.fact_work_end_at) { // если отметка сделана
        return dayjs.utc(shifts.value[0]?.fact_work_end_at).format('HH:mm')
    } else if (!shifts.value[0]?.fact_work_end_at && shifts.value[0]?.violation?.end_status === msg[0]) { // нет отметки и статус warning
        return '??:??'
    }
})

const msg = ['warning', 'moderate', 'ok']

const dayColor = computed(() => {
    if (!shifts.value.length || !isWorkday.value) return ''
    let shift = shifts.value[0]
    // if(!isWorkday.value && dayjs(shift.date).isBefore(dayjs())) return 'bg-ok'
    if (outShift.value) return 'bg-outer'

    if (shifts.value.length > 1) {
        shift = shifts.value.find(shift => shift.violation?.general_status === msg[0])!
        if (!shift) shift = shifts.value.find(shift => shift.violation?.general_status === msg[1])!
        if (!shift) shift = shifts.value.find(shift => shift.violation?.general_status === msg[2])!
    }
    return `bg-${shift?.violation?.general_status}`
})

</script>

<style>
.outer {
    color: #b4b4b4;
    cursor: help
}

.tooltip {
    display: none;
}

.item:hover .tooltip {
    display: block;
}
</style>
